import React, { useEffect, useMemo, useState } from "react";
import MaterialIcon from "../../../components/MaterialIcon";
import PackageTile from "../../../components/PackageTile";
import { getMarketingPrices, labelForKjuupIoFeature } from "../../../services/PaymentService";
import { priceMajor, t } from "../../Subscription/SubscriptionPage";
import { Card, Skeleton } from "./CockpitPrimitives";
import { OfferChoice, OfferFeatures } from "./CockpitOfferParts";

const SIZE_SMALL = "small";
const SIZE_LARGE = "large";
const BILLING_MONTHLY = "monthly";
const BILLING_YEARLY = "yearly";
// Fixed on kjuup.de/pakete as well; the Admin Center only edits the amounts.
const INTRO_MONTHS = 3;
const DEFAULT_MONTHLY_PREMIUM_PERCENT = 10;

// The catalogue holds the yearly-commitment rate; paying monthly adds the
// Admin Center surcharge. Same rule (and rounding) as kjuup.de/pakete.
const withMonthlyPremium = (amount, yearly, premiumPercent) =>
  yearly ? amount : Math.round(amount * (1 + premiumPercent / 100) * 100) / 100;

const wholeEuros = (amount) => priceMajor({ amount: Math.ceil(amount) * 100, currency: "eur" });

/**
 * Kjuup+ offer on the plain Kjuup cockpit with the website prices (company
 * size, intro price, monthly surcharge) and the feature labels from the Admin
 * Center. Kjuup+ is not booked in-app: the CTA opens the request form.
 */
export default function CockpitKjuupPlusOffer({ strings, language, onRequest }) {
  const [marketing, setMarketing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(SIZE_SMALL);
  const [billing, setBilling] = useState(BILLING_YEARLY);

  useEffect(() => {
    let cancelled = false;
    getMarketingPrices().then(
      (data) => {
        if (cancelled) return;
        setMarketing(data);
        setLoading(false);
      },
      () => {
        if (!cancelled) setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const features = useMemo(() => {
    const fromAdmin = (marketing?.kjuupPlusFeatures || [])
      .map((f) => labelForKjuupIoFeature(f, language))
      .filter(Boolean);
    if (fromAdmin.length) return fromAdmin;
    return [
      strings.cockpit_upsell_plus_f1 || "Kommunikationsaktivitäten aller Mitarbeitenden verwalten",
      strings.cockpit_upsell_plus_f2 || "Bestimmte Benutzergruppen innerhalb der Firma informieren",
      strings.cockpit_upsell_plus_f3 || "Interne Gruppenchats speichern",
    ];
  }, [marketing, language, strings]);

  const prices = marketing?.prices || {};
  const premiumSetting = Number(marketing?.monthlyPremiumPercent);
  const premium = Number.isFinite(premiumSetting) ? premiumSetting : DEFAULT_MONTHLY_PREMIUM_PERCENT;
  const yearly = billing === BILLING_YEARLY;
  const base = Number(size === SIZE_SMALL ? prices.kjuupPlusSmall : prices.kjuupPlus) || 0;
  const introBase = Number(size === SIZE_SMALL ? prices.kjuupPlusSmallIntro : prices.kjuupPlusIntro) || 0;
  const regular = base > 0 ? withMonthlyPremium(base, yearly, premium) : 0;
  const intro = introBase > 0 ? withMonthlyPremium(introBase, yearly, premium) : 0;
  const hasIntro = intro > 0 && intro < regular;

  const whenYearly = strings.cockpit_upsell_plus_when_yearly || "bei jährlicher Zahlung";
  let hint = yearly ? whenYearly : "";
  if (hasIntro) {
    const introHint = t(
      strings,
      "cockpit_upsell_plus_intro_hint",
      { months: INTRO_MONTHS, price: wholeEuros(regular) },
      "erste {months} Monate, danach {price}"
    );
    hint = yearly ? `${introHint} ${whenYearly}` : introHint;
  }

  const sizeOptions = [
    { key: SIZE_SMALL, label: strings.cockpit_upsell_plus_size_small || "1–50 Mitarbeitende" },
    { key: SIZE_LARGE, label: strings.cockpit_upsell_plus_size_large || "51–200 Mitarbeitende" },
  ];
  const billingOptions = [
    { key: BILLING_MONTHLY, label: strings.kjuup_io_plan_monthly || "Monthly" },
    { key: BILLING_YEARLY, label: strings.kjuup_io_plan_yearly || "Yearly" },
  ];

  return (
    <Card className="cockpit-offer cockpit-offer--plus">
      <div className="cockpit-offer-pitch">
        <div className="cockpit-offer-brand">
          <PackageTile pkg="plus" size="sm" />
          <span className="cockpit-eyebrow cockpit-eyebrow--accent">Kjuup+</span>
          {hasIntro ? (
            <span className="cockpit-offer-badge">
              {t(strings, "cockpit_upsell_plus_badge", { months: INTRO_MONTHS }, "EINFÜHRUNGSPREIS FÜR {months} MONATE")}
            </span>
          ) : null}
        </div>
        <h3 className="cockpit-upsell-title">{strings.cockpit_upsell_plus_title || "Kjuup+ für Unternehmen"}</h3>
        <p className="cockpit-upsell-text">
          {strings.cockpit_upsell_plus_text ||
            "Verwalte Mitarbeitende, Schreibrechte und die Firmenkommunikation an einem Ort. Interne Kanäle bleiben bei deinen Mitarbeitenden, externe Kanäle verbinden dich mit Projektpartnern, und bestimmte Gruppen informierst du gezielt. Zusätzlich zu Kjuup+ kannst du Kjuup.io-Zugänge für Mitarbeitergruppen buchen. Kjuup+ und Kjuup.io sind kombinierbar."}
        </p>
        <OfferFeatures
          title={strings.kjuup_io_features_title || "Was du bekommst:"}
          features={features}
          loading={loading}
        />
      </div>

      <div className="cockpit-offer-checkout">
        <OfferChoice
          label={strings.cockpit_upsell_plus_size || "Unternehmensgröße"}
          options={sizeOptions}
          value={size}
          onChange={setSize}
        />
        <OfferChoice
          label={strings.cockpit_upsell_plus_billing || "Zahlweise"}
          ariaLabel={strings.kjuup_io_billing_period_aria}
          options={billingOptions}
          value={billing}
          onChange={setBilling}
        />
        <div aria-live="polite">
          {loading ? (
            <Skeleton height={22} width={160} />
          ) : regular > 0 ? (
            <>
              <div className="cockpit-offer-price cockpit-offer-price--inline">
                <span className="cockpit-offer-price-main">
                  {hasIntro ? <span className="cockpit-offer-price-strike">{wholeEuros(regular)}</span> : null}
                  <span className="cockpit-offer-price-net">{wholeEuros(hasIntro ? intro : regular)}</span>
                </span>
                <span className="cockpit-offer-price-unit">
                  {strings.cockpit_upsell_plus_per_company || "/ Firma · Monat zzgl. MwSt."}
                </span>
              </div>
              {hint ? <div className="cockpit-offer-sub cockpit-offer-sub--accent">{hint}</div> : null}
            </>
          ) : null}
        </div>
        <ul className="cockpit-offer-terms">
          <li>{strings.cockpit_upsell_plus_onboarding || "Kosten Onboarding/Einführung abhängig von Firmengröße"}</li>
          <li>{strings.cockpit_upsell_plus_larger || "Preise für größere Firmen auf Anfrage"}</li>
        </ul>
        <button type="button" className="cockpit-offer-cta" onClick={onRequest}>
          <span>{strings.cockpit_upsell_plus_cta || "Unverbindlich anfragen"}</span>
          <MaterialIcon icon="open_in_new" size={16} color="currentColor" weight={400} />
        </button>
      </div>
    </Card>
  );
}
